<template>
  <div>
    <div class="content-header">
      <div class="container-fluid">
        <div class="row mb-2">
          <div class="col-sm-6">
            <h1 class="m-0">{{ this.$route.name }}</h1>
          </div>
          <div class="col-sm-6">
            <ol class="breadcrumb float-sm-right">
              <li class="breadcrumb-item active">
                <i v-bind:class="routeIcon"></i> {{ $t(this.$route.name) }}
              </li>
            </ol>
          </div>
        </div>
      </div>
    </div>

    <section class="content">
      <div class="container-fluid">
        <div class="row col-lg-12">
          <div class="form-group col-sm-2">
            <EditForm
              :default-values="{}"
              create
              title="Create source"
              @finish="handleItemsUpdate"
            >
              <template #button="{onclick}">
                <b-button type="button" variant="success" @click="onclick"
                  ><i class="fas fa-plus"></i> Create</b-button
                >
              </template>
            </EditForm>
          </div>
        </div>

        <b-row class="col-lg-12">
          <div
            class="col-lg-3 col-md-3 col-sm-12"
            v-for="{ id, type, label, options, loaded, multiple, internalSearch } in filterInputsWithOptions"
            v-bind:key="id"
          >
            <b-form-group
              v-if="type === 'input'"
              label-cols-lg="3"
              :label-for="id + '-filter-input'"
              :label="label"
            >
              <b-form-input
                :id="id + '-filter-input'"
                v-model="filters[id]"
                autocomplete="off"
                type="text"
                :placeholder="getFilterPlaceholder(id, label)"
              />
            </b-form-group>
            <b-form-group
              v-if="type === 'select'"
              label-cols-lg="3"
              :label-for="id + '-filter-input'"
              :label="label"
            >
              <multiselect
                :id="id + '-filter-input'"
                v-model="filters[id]"
                :disabled="!loaded"
                :loading="!loaded"
                :options="options"
                label="name"
                track-by="id"
                :multiple="!!multiple"
                :internal-search="!!internalSearch"
                :close-on-select="!multiple"
                :hide-selected="!!multiple"
                :placeholder="'Select ' + label"
              />
            </b-form-group>
          </div>
        </b-row>

        <b-row class="col-lg-12">
          <div class="col-lg-12 col-md-12 col-sm-12 apply-clear">
            <FilterButtons
              :cancel="this.cancel"
              :loadFn="loadItems"
              :cancelFn="cancelRequest"
              :resetFn="clearFilters"
            />
          </div>
        </b-row>

        <div class="row col-12" id="loading-container">
          <div class="col-12 mb-10">
            <div class="mb-10">
              <div class="scroll-wrapper">
                <div class="scroll"></div>
              </div>
              <div class="table-wrapper mb-10">
                <b-table
                  :striped="true"
                  :small="true"
                  :hover="true"
                  :empty-text="emptyText"
                  show-empty
                  :sort-by="sortBy"
                  :sort-desc="sortDesc"
                  :busy="!!cancel"
                  :items="items"
                  :fields="fields"
                  :no-local-sorting="true"
                  :sort-icon-left="true"
                  @sort-changed="sortingChanged"
                >
                  <template #cell(source)="row">
                    {{ row.item.ip ? `${row.item.ip}:${row.item.port}` : '' }}
                  </template>
                  <template #cell(proxy)="row">
                    <div v-if="row.item.proxy">
                      proxy id: {{ row.item.proxy }} <br />
                      proxy port: {{ row.item.proxyPort }}
                    </div>
                  </template>
                  <template #cell(gameType)="row">
                    {{ $store.getters['gameTypes/getName'](row.item.gameType) }}
                  </template>
                  <template #cell(actions)="row">
                    <EditForm
                      :default-values="row.item"
                      title="Edit source"
                      @finish="handleItemsUpdate"
                    />
                    <RemoveForm
                      :object="row.item.name"
                      message="Please confirm removing source"
                      :url="'/api/v1/sources/' + row.item.id"
                      @finish="handleItemsUpdate"
                    />
                  </template>
                </b-table>
              </div>
            </div>

            <div class="row mb-10">
              <div class="col-12">
                <b-pagination
                  class="float-right"
                  v-model="pagination.page"
                  :total-rows="pagination.records"
                  :per-page="pagination.onPage"
                  @change="onPageChange"
                  first-number
                  last-number
                />
              </div>
            </div>

            <div class="row mb-10">
              <div class="col-12">
                <ve-pagination
                  class="float-right"
                  :page-index="pagination.page"
                  :page-size="pagination.onPage"
                  :total="pagination.records"
                  :pagingCount="pagination.count"
                  :layout="['total', 'sizer']"
                  :pageSizeOption="pagination.pageSizeOption"
                  @on-page-size-change="onPageSizeChange"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import EditSourceForm from '../../components/Sources/EditSourceForm.vue'
import RemoveForm from '../../components/remove-form/RemoveForm.vue'
import axios from 'axios'
import { getRouteIcon, ROUTE_SOURCES } from '../../utils/routerIcons'
import FilterButtons from '../../components/FilterButtons'
import Multiselect from 'vue-multiselect'

function getDefaultPagination() {
  return {
    onPage: 25,
    page: 1,
    records: 0,
    count: 5,
    pageSizeOption: [25, 50, 100, 200]
  }
}

// function jsonFormatter(value) {
//   const txt = JSON.stringify(value);
//
//   return txt.length <= 23 ? txt : `${txt.substr(0, 20)}...`;
// }

export default {
  components: {
    EditForm: EditSourceForm,
    RemoveForm,
    FilterButtons,
    Multiselect
  },
  data() {
    return {
      cancel: null,
      error: null,
      fields: [
        { key: 'id', label: '_ID', sortable: true },
        { key: 'name', label: 'Name', sortable: true },
        { key: 'order', label: 'Order', sortable: true },
        { key: 'source', label: 'Source' },
        // { key: 'ip', label: 'IP', },
        // { key: 'port', label: 'Port', },
        // { key: 'timeout', label: 'Timeout', },
        { key: 'proxy', label: 'Proxy' },
        // { key: 'proxyPort', label: 'Proxy Port', },
        // { key: 'player', label: 'Player', formatter: jsonFormatter},
        { key: 'gameType', label: 'Game Type' },
        { key: 'actions', label: 'Actions' }
      ],
      items: [],
      sortBy: null,
      sortDesc: null,
      filters: {
        name: null,
        source: null,
        order: null,
        gameType: []
      },
      pagination: getDefaultPagination(),
      filterInputs: [
        { id: 'name', type: 'input', label: 'Name' },
        { id: 'source', type: 'input', label: 'Source' },
        { id: 'order', type: 'input', label: 'Order' },
        {
          id: 'gameType',
          type: 'select',
          label: 'Game Type',
          options: [],
          loaded: false,
          multiple: true,
          internalSearch: true
        }
      ]
    }
  },
  computed: {
    filterInputsWithOptions() {
      return this.filterInputs.map(filter => {
        if (filter.id === 'gameType') {
          return {
            ...filter,
            options: this.$store.getters['gameTypes/list'].map(({ name, id }) => ({
              name,
              id
            })),
            loaded: this.$store.getters['gameTypes/loaded']
          }
        }

        return filter
      })
    },
    emptyText() {
      if (this.cancel) {
        return 'Loading items..'
      }

      if (this.error) {
        return this.error
      }

      return 'There are no records to show'
    },
    routeIcon: () => getRouteIcon(ROUTE_SOURCES)
  },
  methods: {
    sortingChanged(ctx) {
      this.sortBy = ctx.sortBy
      this.sortDesc = ctx.sortDesc
      this.pagination.page = 1

      this.loadItems()
    },
    handleItemsUpdate() {
      this.loadItems()
    },
    clearFilters() {
      this.filters = {
        name: null,
        source: null,
        order: null,
        gameType: []
      }
      this.sortingChanged({
        sortBy: null,
        sortDesc: null
      })
    },
    getFilterPlaceholder(id, label) {
      if (id === 'source') {
        return 'Enter IP or IP:Port (full port after colon)'
      }

      if (id === 'order') {
        return 'Separate by comma'
      }

      return 'Enter ' + label
    },
    getFilterParams() {
      const params = {
        name: this.filters.name || null,
        source: this.filters.source || null,
        order:
          this.filters.order !== null && String(this.filters.order).trim() !== ''
            ? String(this.filters.order).trim()
            : null,
        gameType: this.filters.gameType.length
          ? this.filters.gameType.map(({ id }) => id)
          : null
      }

      return Object.fromEntries(
        Object.entries(params).filter(([, value]) => {
          if (Array.isArray(value)) {
            return value.length > 0
          }

          return value !== null && value !== ''
        })
      )
    },
    loadItems() {
      let loadingInstance = this.$veLoading({
        target: document.querySelector('#loading-container'),
        name: 'wave'
      })

      this.items = []

      let self = this
      loadingInstance.show()

      this.$http({
        url: '/api/v1/sources',
        params: {
          ...this.getFilterParams(),
          // pagination
          limit: this.pagination.onPage,
          offset: this.pagination.onPage * (this.pagination.page - 1),

          // sort params
          orderBy: this.sortBy,
          direction: this.sortDesc ? 'DESC' : 'ASC'
        },
        method: 'GET',
        cancelToken: new axios.CancelToken(c => {
          self.cancel = c
        })
      })
        .then(resp => {
          let {
            items,
            pagination: { limit, totalCount }
          } = resp.data

          this.items = items
          this.pagination.onPage = limit
          this.pagination.records = totalCount
        })
        .catch(err => {
          this.items = []
          this.error = err
        })
        .finally(() => {
          loadingInstance.close()
          this.cancel = null
        })
    },
    cancelRequest() {
      if (this.cancel) {
        this.cancel('this -> cancel previous request')
      }
    },
    onPageChange(page) {
      this.pagination.page = page
      this.loadItems()
    },
    onPageSizeChange(pageSize) {
      this.pagination.page = 1
      this.pagination.onPage = pageSize
      this.loadItems()
    },
    onFinishEdit() {
      this.loadItems()
    }
  },
  async beforeMount() {
    await this.$store.dispatch('gameTypes/loadList')
  },
  mounted() {
    this.loadItems()
    this.$store.dispatch('tablesForSelect/loadList')
  }
}
</script>
